import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SELF_SERVE_ONBOARDING_ENABLED } from "@/config/featureFlags";
import { buildAdminUrl } from "@/lib/adminOrigin";
import AuthShowcase from "./AuthShowcase";

const BOOK_A_DEMO_URL = "https://www.usedibs.com/listing/f5164d5a-89b2-4503-894b-31a924c83491";

// Password validation helpers
const hasMinLength = (pw: string) => pw.length >= 8;
const hasLowercase = (pw: string) => /[a-z]/.test(pw);
const hasUppercase = (pw: string) => /[A-Z]/.test(pw);
const hasNumber = (pw: string) => /[0-9]/.test(pw);
const hasSpecialChar = (pw: string) => /[^a-zA-Z0-9]/.test(pw);

const Auth = () => {
  const { user, loading, signInWithGoogle, signInWithEmail, signUpWithEmail, resetPasswordForEmail, signOut, isPasswordRecovery } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Staff invite mode: pre-fill email and lock it
  const staffEmail = searchParams.get("staff_email")?.toLowerCase() || null;
  const isStaffInvite = !!staffEmail;

  // Self-serve signup is only allowed when the feature flag is on. Staff invites
  // must always be able to set their password, so they bypass the flag.
  const signupAllowed = SELF_SERVE_ONBOARDING_ENABLED || isStaffInvite;
  const signupRequested =
    searchParams.get("mode") === "signup" || searchParams.get("source") === "free_trial";

  const [activeTab, setActiveTab] = useState(
    signupAllowed && signupRequested ? "signup" : "login"
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotEmailSent, setForgotEmailSent] = useState(false);
  const [failedLoginAttempts, setFailedLoginAttempts] = useState(0);
  const [lastFailedEmail, setLastFailedEmail] = useState("");

  useEffect(() => {
    const mode = searchParams.get("mode");
    if (mode === "login") {
      setActiveTab("login");
    } else if (signupAllowed && signupRequested) {
      setActiveTab("signup");
    }
    const error = searchParams.get("error");
    if (error === "new_account_via_login") {
      if (signupAllowed) {
        toast.error("You do not have an existing account with us. Please sign up first.", { duration: 5000 });
        setActiveTab("signup");
      } else {
        toast.error("You do not have an existing account with us. Please book a demo to get set up.", { duration: 6000 });
        setActiveTab("login");
      }
      // Clean up the URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("error");
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [searchParams, navigate, signupAllowed, signupRequested]);

  const [loginEmail, setLoginEmail] = useState(staffEmail || "");
  const [loginPassword, setLoginPassword] = useState("");
  const [signupEmail, setSignupEmail] = useState(staffEmail || "");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");

  const checks = useMemo(() => ({
    minLength: hasMinLength(signupPassword),
    hasLowercase: hasLowercase(signupPassword),
    hasUppercase: hasUppercase(signupPassword),
    hasNumber: hasNumber(signupPassword),
    hasSpecial: hasSpecialChar(signupPassword),
    passwordsMatch: signupPassword.length > 0 && signupConfirmPassword.length > 0 && signupPassword === signupConfirmPassword,
  }), [signupPassword, signupConfirmPassword]);

  const allChecksPassed = checks.minLength && checks.hasLowercase && checks.hasUppercase && checks.hasNumber && checks.hasSpecial && checks.passwordsMatch;

  const from = location.state?.from || "/admin";

  const navigateToDestination = () => {
    const isAdminSubdomain = window.location.hostname.startsWith('admin.');
    let dest = "/";
    if (searchParams.get("source") === "free_trial") {
      dest = "/?tab=settings&settings=billing";
    }

    if (from === "/admin") {
      if (isAdminSubdomain) {
        navigate(dest, { replace: true });
      } else {
        window.location.href = buildAdminUrl(window.location, dest);
      }
    } else {
      navigate(from, { replace: true });
    }
  };

  useEffect(() => {
    const handleAuthRedirect = async () => {
      if (!loading && user) {
        // If this is a password recovery flow, redirect to the reset-password page
        if (isPasswordRecovery) {
          navigate("/auth/reset-password", { replace: true });
          return;
        }

        // If there's an active session but they are trying to accept a staff invite 
        // for a DIFFERENT email, sign them out to start a fresh session.
        if (staffEmail && user.email?.toLowerCase() !== staffEmail.toLowerCase()) {
          toast("Signing out of current account to set up your staff account...", { duration: 4000 });
          await signOut();
          return; // Stay on the auth page so they can sign up/in
        }

        navigateToDestination();
      }
    };

    handleAuthRedirect();
  }, [user, loading, navigate, from, isPasswordRecovery, staffEmail, signOut]);

  const handleGoogleSignIn = async (intent: "login" | "signup") => {
    localStorage.setItem("dibs_oauth_intent", intent);
    setIsSubmitting(true);
    const { error } = await signInWithGoogle();
    if (error) {
      toast.error(error.message);
    }
    setIsSubmitting(false);
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);
    const { data, error } = await signInWithEmail(loginEmail, loginPassword);
    if (error) {
      if (error.message.includes("Email not confirmed")) {
        toast.error("Please confirm your email address before signing in.");
      } else {
        if (error.message.includes("Invalid login credentials")) {
          const currentEmail = loginEmail.toLowerCase();
          let currentAttempts = failedLoginAttempts;
          
          if (currentEmail !== lastFailedEmail) {
            currentAttempts = 0;
            setLastFailedEmail(currentEmail);
          }
          
          const newAttempts = currentAttempts + 1;
          setFailedLoginAttempts(newAttempts);
          
          if (newAttempts >= 5) {
            toast.error("Too many failed attempts. Let's reset your password.");
            setForgotEmail(currentEmail);
            setShowForgotPassword(true);
            setFailedLoginAttempts(0);
          } else {
            toast.error(error.message);
          }
        } else {
          toast.error(error.message);
        }
      }
    } else if (data?.session) {
      setFailedLoginAttempts(0);
      const emailConfirmedAt = data.session.user.email_confirmed_at || data.session.user.identities?.find(i => i.provider === 'email')?.updated_at;
      const { data: business } = await supabase.from('businesses').select('confirmed_email').eq('user_id', data.session.user.id).maybeSingle();

      // Check if this user is a staff member. Staff skip email confirmation
      // (accept-staff-invite already proved they control the inbox). Matched on
      // user_id because that's what the staff self-read policy scopes on.
      const { data: staffCheck } = await supabase
        .from('staff_members')
        .select('id')
        .eq('user_id', data.session.user.id)
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();

      const isStaffUser = !!staffCheck;

      if (!isStaffUser && !emailConfirmedAt && !business?.confirmed_email) {
        await signOut();
        toast.error("Please confirm your email address before signing in.");
        setIsSubmitting(false);
        return;
      }

      navigateToDestination();
    }
    setIsSubmitting(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Please enter your email address");
      return;
    }
    setIsSubmitting(true);
    try {
      const { error } = await resetPasswordForEmail(forgotEmail);
      if (error) throw error;
      setForgotEmailSent(true);
      // Keep the response identical whether the address is unknown, uses
      // OAuth, or supports password sign-in. This prevents account/provider
      // enumeration from the public forgot-password form.
      toast.success("If this email can use password sign-in, a reset link is on its way.");
    } catch (error: any) {
      toast.error(error.message || "Failed to send reset link");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signupEmail || !signupPassword || !signupConfirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!allChecksPassed) {
      toast.error("Please meet all password requirements");
      return;
    }

    setIsSubmitting(true);

    const { data, error } = await signUpWithEmail(signupEmail, signupPassword);

    // When email enumeration is disabled in Supabase, signing up with an existing email 
    // returns success but with an empty identities array for the user.
    if (data?.user && data.user.identities && data.user.identities.length === 0) {
      toast.error("This email is already registered. Please sign in instead.");
      setIsSubmitting(false);
      return;
    }

    if (error) {
      if (error.message.includes("already registered")) {
        toast.error("This email is already registered. Please sign in instead.");
      } else if (error.message.includes("registered as a staff account")) {
        toast.error(
          "This email is a staff account on Dibs. Open the invitation link the business owner emailed you instead of creating a new account."
        );
      } else {
        toast.error(error.message);
      }
    } else if (isStaffInvite) {
      // Staff invites skip email confirmation. Sign them in immediately
      toast.success("Account created! Signing you in...");
      const { error: signInError } = await signInWithEmail(signupEmail, signupPassword);
      if (signInError) {
        toast.error("Account created but auto-login failed. Please sign in manually.");
        setActiveTab("login");
      }
      // If sign-in succeeds, the useEffect above will navigate to the dashboard
    } else {
      toast.success("Account created! 📩 Please check your email inbox and click the confirmation link to activate your account before logging in.");
      setActiveTab("login");
    }
    setIsSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <div className="text-center space-y-1">
          <p className="text-sm font-medium text-foreground">Signing you in...</p>
          <p className="text-xs text-muted-foreground">Please wait a moment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background grid lg:grid-cols-2">
      <AuthShowcase />

      <div className="relative flex flex-col items-center justify-center px-4 py-10">
        <a href="/" className="mb-8 flex items-center gap-2 lg:hidden hover:opacity-80 transition-opacity">
          <img
            src="/dibs_assets/red_logo_with_transparent_bg.png"
            alt="Dibs"
            className="h-9 object-contain"
          />
          <span className="text-primary font-heading font-bold text-2xl tracking-tight">Dibs</span>
        </a>

        <div className="w-full max-w-md animate-fade-up">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-heading font-bold">{isStaffInvite ? "Staff account setup" : "Admin dashboard"}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{isStaffInvite ? "Create a password to access your staff dashboard" : "Sign in to manage your business"}</p>
        </div>
        <div>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {signupAllowed && (
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Log in</TabsTrigger>
                <TabsTrigger value="signup">Sign up</TabsTrigger>
              </TabsList>
            )}

            <TabsContent value="login" className="space-y-4 mt-4">
              {showForgotPassword ? (
                forgotEmailSent ? (
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30 p-4">
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-green-800 dark:text-green-300">Reset link sent!</p>
                        <p className="text-sm text-green-700 dark:text-green-400/80">
                          We've sent a password reset link to <span className="font-medium">{forgotEmail}</span>. Check your inbox and click the link to set your new password.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="text-sm text-primary hover:underline"
                      onClick={() => { setShowForgotPassword(false); setForgotEmailSent(false); setForgotEmail(""); }}
                    >
                      ← Back to sign in
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <p className="text-sm font-body text-muted-foreground">Enter your email address and we'll send you a link to reset your password.</p>
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email">Email</Label>
                      <Input
                        id="forgot-email"
                        type="email"
                        placeholder="you@example.com"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Send reset link
                    </Button>
                    <button
                      type="button"
                      className="text-sm text-primary hover:underline w-full text-center"
                      onClick={() => { setShowForgotPassword(false); setForgotEmail(""); }}
                    >
                      ← Back to sign in
                    </button>
                  </form>
                )
              ) : (
                <>
                  <form onSubmit={handleEmailSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@example.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        disabled={isSubmitting || isStaffInvite}
                        readOnly={isStaffInvite}
                        className={isStaffInvite ? 'text-muted-foreground cursor-not-allowed' : ''}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-primary transition-colors"
                        onClick={() => { setShowForgotPassword(true); setForgotEmail(loginEmail); }}
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Sign in
                    </Button>
                  </form>

                  {!isStaffInvite && (
                    <>
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                        </div>
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => handleGoogleSignIn("login")}
                        disabled={isSubmitting}
                      >
                        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                          <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                          <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                          <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                          <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Continue with Google
                      </Button>
                    </>
                  )}
                </>
              )}
            </TabsContent>

            {signupAllowed && (
            <TabsContent value="signup" className="space-y-4 mt-4">
              <form onSubmit={handleEmailSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="you@example.com"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    disabled={isSubmitting || isStaffInvite}
                    readOnly={isStaffInvite}
                    autoComplete="off"
                    className={isStaffInvite ? 'text-muted-foreground cursor-not-allowed' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="••••••••"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    disabled={isSubmitting}
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-confirm-password">Confirm password</Label>
                  <Input
                    id="signup-confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={signupConfirmPassword}
                    onChange={(e) => setSignupConfirmPassword(e.target.value)}
                    disabled={isSubmitting}
                    autoComplete="new-password"
                  />
                </div>

                {/* Password requirements checklist */}
                <div className="space-y-1.5 pt-1">
                  <div className="flex items-center gap-2">
                    {checks.minLength ? <Check className="w-3.5 h-3.5 text-green-500 shrink-0" /> : <X className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />}
                    <span className={`text-xs ${checks.minLength ? "text-green-600" : "text-muted-foreground/60"}`}>At least 8 characters</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {checks.hasLowercase ? <Check className="w-3.5 h-3.5 text-green-500 shrink-0" /> : <X className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />}
                    <span className={`text-xs ${checks.hasLowercase ? "text-green-600" : "text-muted-foreground/60"}`}>Contains a lowercase letter (a-z)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {checks.hasUppercase ? <Check className="w-3.5 h-3.5 text-green-500 shrink-0" /> : <X className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />}
                    <span className={`text-xs ${checks.hasUppercase ? "text-green-600" : "text-muted-foreground/60"}`}>Contains an uppercase letter (A-Z)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {checks.hasNumber ? <Check className="w-3.5 h-3.5 text-green-500 shrink-0" /> : <X className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />}
                    <span className={`text-xs ${checks.hasNumber ? "text-green-600" : "text-muted-foreground/60"}`}>Contains a number (0-9)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {checks.hasSpecial ? <Check className="w-3.5 h-3.5 text-green-500 shrink-0" /> : <X className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />}
                    <span className={`text-xs ${checks.hasSpecial ? "text-green-600" : "text-muted-foreground/60"}`}>Contains a special character (!@#$...)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {checks.passwordsMatch ? <Check className="w-3.5 h-3.5 text-green-500 shrink-0" /> : <X className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />}
                    <span className={`text-xs ${checks.passwordsMatch ? "text-green-600" : "text-muted-foreground/60"}`}>Passwords match</span>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isSubmitting || !allChecksPassed}>
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create account
                </Button>
              </form>

              {!isStaffInvite && (
                <>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => handleGoogleSignIn("signup")}
                    disabled={isSubmitting}
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                  </Button>
                </>
              )}
            </TabsContent>
            )}
          </Tabs>
          {!signupAllowed && (
            <p className="mt-6 text-center text-sm text-muted-foreground">
              New to Dibs?{" "}
              <a
                href={BOOK_A_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-medium hover:underline"
              >
                Book a demo
              </a>{" "}
              to get set up.
            </p>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
